import { AssistantService } from "shared/services/AssistantService";
import {
  AssistantActionCreatePortfolioType,
  AssistantAttachment,
  AssistantEsgPreferencesType,
  AssistantMessage,
} from "types/AssistantTypes";
import { create } from "./store";
import { AI_PRO_ADVISOR } from "constants/AiProAdvisor";
import { createLogger } from "shared/logger";

const log = createLogger("Threads");

type ThreadsStoreState = {
  attachments: Record<string, AssistantAttachment[]>;
  messages: Record<string, AssistantMessage[]>;
  esgPreferences: Record<string, AssistantEsgPreferencesType>;
  actions: Record<string, AssistantActionCreatePortfolioType>;
  blockInput: Record<string, boolean>;
  fromHistory: boolean;
  hideTempStatusUpdate: boolean;
  stories: Record<string, Record<string, string>>;
};

type ThreadsStoreActions = {
  appendMessages: (threadId: string, messages: AssistantMessage[]) => void;
  appendMessageDelta: (threadId: string, messageDelta: string) => void;
  clearMessage: (threadId: string) => void;
  appendAgentStatusMessageDelta: (
    threadId: string,
    messageDelta: string,
    type: string,
  ) => void;
  appendEsgPreferences: (
    threadId: string,
    esgConstraint: AssistantEsgPreferencesType,
  ) => void;
  appendAction: (
    threadId: string,
    action: AssistantActionCreatePortfolioType,
  ) => void;
  resetAction: (threadId: string) => void;
  resetThread: (threadId: string) => void;
  setBlockInput: (threadId: string, blockInput: boolean) => void;
   setFromHistory: (isFromHistory: boolean) => void;
  setHideTempStatusUpdate: (hideTempStatusUpdate: boolean) => void;
  fetchStory: (threadId: string, portfolioId: string) => void;
};

const initialData: ThreadsStoreState = {
  attachments: {},
  messages: {},
  esgPreferences: {},
  actions: {},
  blockInput: {},
  fromHistory: false,
  hideTempStatusUpdate: false,
  stories: {},
};

const useThreadsStore = create<ThreadsStoreState & ThreadsStoreActions>()(
  (set, get) => ({
    ...initialData,

    appendMessages: async (threadId, messages) => {
      const currentMessages = get().messages[threadId] || [];
      const messageIds = currentMessages.map((m) => m.id);

      const messagesWithoutTemp = currentMessages.filter(
        (message) =>
          message.id !== "temp" && message.id !== "temp-status-update",
      );

      const deduplicatedMessages = messages.filter(
        (m) => !messageIds.includes(m.id) && m.thread_id === threadId,
      );

      set({
        messages: {
          ...get().messages,
          [threadId]: [...messagesWithoutTemp, ...(deduplicatedMessages || [])],
        },
      });
    },
    appendMessageDelta: async (threadId, messageDelta) => {
      const currentMessages = get().messages[threadId] || [];

      if (
        currentMessages.length > 0 &&
        currentMessages[currentMessages.length - 1].id === "temp"
      ) {
        const messagesWithoutTemp = currentMessages.filter(
          (message) => message.id !== "temp-status-update",
        );
        const updatedMessages = messagesWithoutTemp.map((msg, index) => {
          if (index === currentMessages.length - 1) {
            const updatedContent = [...msg.content];
            const existingTextValue = updatedContent[0]?.text?.value ?? "";
            updatedContent[0] = {
              ...updatedContent[0],
              text: {
                ...updatedContent[0].text,
                value: existingTextValue + messageDelta,
              },
            };
            return {
              ...msg,
              content: updatedContent,
            };
          }
          return msg;
        });

        set({
          messages: {
            ...get().messages,
            [threadId]: updatedMessages,
          },
        });
      } else {
        const messagesWithoutTemp = currentMessages.filter(
          (message) => message.id !== "temp-status-update",
        );
        const currentTime = new Date().toISOString();
        const newMessage: AssistantMessage = {
          object: "message",
          id: "temp",
          thread_id: threadId,
          role: "assistant" as const,
          artifacts: [],
          status: "completed" as const,
          created_at: currentTime,
          updated_at: currentTime,
          metadata: {},
          content: [
            {
              type: "text" as const,
              text: {
                value: messageDelta,
              },
            },
          ],
        };

        set({
          messages: {
            ...get().messages,
            [threadId]: [...messagesWithoutTemp, newMessage],
          },
        });
      }
    },

    clearMessage: async (threadId) => {
      const currentMessages = get().messages[threadId] || [];
      const messagesWithoutTemp = currentMessages.filter(
        (message) => message.id !== "temp",
      );

      set({
        messages: {
          ...get().messages,
          [threadId]: messagesWithoutTemp,
        },
      });
    },
    appendAgentStatusMessageDelta: async (threadId, messageDelta, type) => {
      const currentMessages = get().messages[threadId] || [];

      //** */ Find existing status message
      let existingStatusMessageIndex = currentMessages.findIndex(
        (msg) => msg.id === "temp-status-update",
      );

      //** skip loader functionality if temp is present */
      let existingStatusMessageIndexTemp = currentMessages.findIndex(
        (msg) => msg.id === "temp",
      );
      if (existingStatusMessageIndexTemp !== -1) {
        return;
      }

      let updatedMessages = [...currentMessages];

      //** */ If no existing message, create a new one
      if (existingStatusMessageIndex === -1) {
        const currentTime = new Date().toISOString();
        const newMessage = {
          object: "message" as const,
          id: "temp-status-update",
          thread_id: threadId,
          role: "assistant" as const,
          artifacts: [],
          skeletonLoader: true,
          status: "completed",
          created_at: currentTime,
          updated_at: currentTime,
          metadata: {},
          content: [
            {
              type: "text" as const,
              text: {
                value: `${messageDelta.trim()}`,
              },
            },
          ],
        };

        updatedMessages.push(newMessage);

        set({
          messages: {
            ...get().messages,
            [threadId]: updatedMessages,
          },
        });

        return; // Exit early, since we've already created a new message
      }

      // If existing message found, update it

      const updatedContent = [
        ...updatedMessages[existingStatusMessageIndex].content,
      ];
      let existingTextValue = updatedContent[0]?.text?.value ?? "";
      let lines = existingTextValue
        .split("\n")
        .filter((line) => line.trim() !== ""); // Remove empty lines

      if (type === AI_PRO_ADVISOR.SET_TASK_UPDATES) {
        // Always update the first line with the latest task update
        if (lines.length > 0) {
          lines[0] = `${messageDelta.trim()}`;
        } else {
          lines.push(`${messageDelta.trim()}`);
        }
      } else if (type === AI_PRO_ADVISOR.TOOL_USE) {
        const isCompleted =
          messageDelta.trim() === AI_PRO_ADVISOR.VINCOLI_COMPLETED ||
          messageDelta.trim() === AI_PRO_ADVISOR.CREATE_PORTFOLIO_COMPLETED ||
          messageDelta.trim() ===
          AI_PRO_ADVISOR.CREATE_PORTFOLIO_COMMENT_COMPLETED;

        const inProgressMessages = [
          AI_PRO_ADVISOR.VINCOLI_IN_PROGRESS,
          AI_PRO_ADVISOR.CREATE_PORTFOLIO_IN_PROGRESS,
          AI_PRO_ADVISOR.CREATE_PORTFOLIO_COMMENT_IN_PROGRESS,
        ];

        // Remove "in progress" message if we get a "completed" message
        if (isCompleted) {
          lines = lines.filter(
            (line) =>
              !inProgressMessages.some((inProgress) =>
                line.includes(inProgress),
              ),
          );

          lines.push(`[CHECK_ICON]${messageDelta.trim()}`);
        } else {
          lines.push(`${messageDelta.trim()}`);
        }
      }
      // Update message content
      updatedContent[0] = {
        ...updatedContent[0],
        text: {
          ...updatedContent[0].text,
          value: lines.join("\n"),
        },
      };

      // Apply update to the messages array
      updatedMessages[existingStatusMessageIndex] = {
        ...updatedMessages[existingStatusMessageIndex],
        content: updatedContent,
      };

      // Ensure state updates with new messages
      set({
        messages: {
          ...get().messages,
          [threadId]: updatedMessages,
        },
      });
    },

    appendEsgPreferences: async (threadId, esgPreferences) => {
      set({
        esgPreferences: {
          [threadId]: esgPreferences,
        },
      });
    },
    appendAction: async (threadId, action) => {
      set({
        actions: {
          [threadId]: action,
        },
      });
    },
    resetAction: (threadId) => {
      set({
        actions: {
          [threadId]: null,
        },
      });
    },
    resetThread: (threadId) => {
      set({
        messages: {
          ...get().messages,
          [threadId]: [],
        },
        actions: {
          [threadId]: null,
        },
        hideTempStatusUpdate: false,
        attachments: { ...get().attachments, [threadId]: [] },
        blockInput: { ...get().blockInput, [threadId]: false },
        // hasUserScrolled: false,
      });
    },
    setBlockInput: async (threadId, blockInput) => {
      set({
        blockInput: {
          ...get().blockInput,
          [threadId]: blockInput,
        },
      });
    },
    setFromHistory: (isFromHistory) => {
      set({ fromHistory: isFromHistory });
    },
    setHideTempStatusUpdate: (hideTempStatusUpdate: boolean) => {
      set({ hideTempStatusUpdate: hideTempStatusUpdate });
    },
    fetchStory: async (threadId, portfolioId) => {
      try {
        const story = await AssistantService.fetchPortfolioStory(portfolioId);
        set((state) => ({
          stories: {
            ...state.stories,
            [threadId]: {
              ...(state.stories[threadId] || {}),
              [portfolioId]: story,
            },
          },
        }));
      } catch (error) {
        log.error("fetch portfolio story failed", { threadId, portfolioId, error });
      }
    },
  }),
);

export default useThreadsStore;
